import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, MailX } from "lucide-react";
import { api } from "@/lib/api";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [lookup, setLookup] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("This unsubscribe link is missing its token."); return; }
    api.get(`/public/beta/unsubscribe/${encodeURIComponent(token)}`)
      .then((r) => setLookup(r.data))
      .catch(() => setError("This unsubscribe link is not valid."));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/public/beta/unsubscribe", { token });
      setDone(r.data);
    } catch {
      setError("This unsubscribe link is not valid.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wyv-container py-14 max-w-md" data-testid="unsubscribe-page">
      <Card>
        <CardContent className="p-6">
          <h1 className="font-heading font-bold text-xl text-foreground inline-flex items-center gap-2">
            <MailX className="h-5 w-5 text-slate-500" /> Unsubscribe
          </h1>
          {error && <p className="text-sm text-destructive mt-3" data-testid="unsubscribe-error">{error}</p>}
          {!error && !lookup && !done && <p className="text-sm text-muted-foreground mt-3">Checking your link…</p>}
          {done && (
            <div className="mt-3" data-testid="unsubscribe-done">
              <p className="text-sm text-foreground inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-accent" /> {done.message}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Changed your mind? You can rejoin anytime from the <Link to="/beta#signup" className="text-primary hover:underline">signup page</Link>.
              </p>
            </div>
          )}
          {lookup && !done && (
            <div className="mt-3">
              {lookup.already_unsubscribed ? (
                <p className="text-sm text-muted-foreground" data-testid="unsubscribe-already">
                  {lookup.email_masked} is already unsubscribed from WitnessYourVote launch updates.
                </p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Unsubscribe <span className="font-medium text-foreground">{lookup.email_masked}</span> from
                    WitnessYourVote launch updates?
                  </p>
                  <Button className="mt-4 w-full" variant="outline" disabled={busy} onClick={confirm}
                          data-testid="unsubscribe-confirm">
                    {busy ? "Unsubscribing…" : "Yes, unsubscribe me"}
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
